import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getValidEnrollment } from '../utils/enrollmentUtils';

function parseEnrollmentTorsList(en) {
  if (!en) return [];
  if (Array.isArray(en.tors)) return en.tors.filter((t) => t && t.account_id);
  if (typeof en.tors === 'string') {
    try {
      const p = JSON.parse(en.tors || '[]');
      return Array.isArray(p) ? p.filter((t) => t && t.account_id) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** All TEACHER_OF_RECORD account ids on this enrollment (`tors[]` from relationships). */
function torAccountIdsFromEnrollment(en) {
  const ids = new Set();
  if (!en) return ids;
  for (const t of parseEnrollmentTorsList(en)) {
    if (t.account_id) ids.add(String(t.account_id));
  }
  return ids;
}

/** First TOR for display: first entry in `tors[]` (relationship order). */
function primaryTorAccountIdFromEnrollment(en) {
  const list = parseEnrollmentTorsList(en);
  return list[0]?.account_id || null;
}

function formatEnrollmentTorDisplay(en, accountIdToDetailsMapping) {
  const id = primaryTorAccountIdFromEnrollment(en);
  if (!id) return 'None';
  const list = parseEnrollmentTorsList(en);
  if (list.length === 1) {
    const t = list[0];
    if (t.first_name && t.last_name) {
      return `${t.first_name} ${t.last_name}${t.email ? ` (${t.email})` : ''}`;
    }
  }
  if (list.length > 1) {
    return list
      .map((t) =>
        t.first_name && t.last_name ? `${t.first_name} ${t.last_name}` : t.account_id
      )
      .join(', ');
  }
  const torAccount = accountIdToDetailsMapping[id];
  if (torAccount) {
    return `${torAccount.first_name} ${torAccount.last_name} (${torAccount.primary_email})`;
  }
  return `TOR ID: ${id}`;
}

// School-specific configuration for CSV import
const CSV_IMPORT_SCHOOL_ID = '2c563b85-6afe-46f2-8918-5b15152d6991';
const CSV_IMPORT_SCHOOL_NAME = 'Scale Leadership Academy East';

const CSVImport = ({ userData }) => {
  const [csvText, setCsvText] = useState('');
  const [validationResults, setValidationResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [associations, setAssociations] = useState([]);
  const [concentrations, setConcentrations] = useState([]);
  const [torEmailMapping, setTorEmailMapping] = useState({}); // email -> account_id
  const [accountIdToDetailsMapping, setAccountIdToDetailsMapping] = useState({}); // account_id -> {first_name, last_name, primary_email}
  const [dryRunResults, setDryRunResults] = useState(null);
  const [updateFilters, setUpdateFilters] = useState({
    ignorePassword: true,
    ignoreFirstName: false,
    ignoreLastName: false,
    ignoreAssociation: false,
    ignoreConcentration: false,
    ignoreGradeLevel: false,
    ignoreTor: false,
    ignoreInactive: false
  });
  const [activeTab, setActiveTab] = useState('validation'); // 'validation' or 'dryRun'
  const [activeValidationSection, setActiveValidationSection] = useState('existing'); // 'existing' or 'new'
  const [activeDryRunFilter, setActiveDryRunFilter] = useState('all'); // 'all', 'new', 'accountUpdate', 'enrollmentUpdate', 'accessCardUpdate', 'noUpdate'
  const [importResult, setImportResult] = useState(null); // { type: 'success' | 'error', message: string }

  // Expected CSV columns (torEmail is optional)
  const expectedColumns = ['lastName', 'firstName', 'gradeLevel', 'faCode', 'email', 'password', 'concentration'];

  // Fetch associations and concentrations on component mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [associationsRes, concentrationsRes] = await Promise.all([
          axios.get('/api/user-management/associations', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/user-management/concentrations', { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        setAssociations(associationsRes.data.associations || []);
        setConcentrations(concentrationsRes.data.concentrations || []);
      } catch (err) {
        console.error('Error fetching reference data:', err);
      }
    };

    fetchReferenceData();
  }, []);

  // Auto-switch validation section if needed
  useEffect(() => {
    if (validationResults) {
      if (activeValidationSection === 'existing' && validationResults.emailCheck.existingAccounts.length === 0) {
        if (validationResults.emailCheck.newEmails.length > 0) {
          setActiveValidationSection('new');
        }
      } else if (activeValidationSection === 'new' && validationResults.emailCheck.newEmails.length === 0) {
        if (validationResults.emailCheck.existingAccounts.length > 0) {
          setActiveValidationSection('existing');
        }
      }
    }
  }, [validationResults, activeValidationSection]);

  // Function to parse CSV text
  const parseCSV = (text) => {
    if (!text.trim()) {
      throw new Error('CSV text is empty');
    }

    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    // Parse header row
    const headerRow = parseCSVLine(lines[0]);
    const headerLower = headerRow.map(h => h.toLowerCase().trim());

    // Validate headers
    const missingColumns = expectedColumns.filter(col => 
      !headerLower.includes(col.toLowerCase())
    );

    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
    }

    // Get column indices for required columns
    const columnIndices = {};
    expectedColumns.forEach(col => {
      const index = headerLower.findIndex(h => h === col.toLowerCase());
      if (index !== -1) {
        columnIndices[col] = index;
      }
    });

    // Check for optional torEmail column
    const torEmailIndex = headerLower.findIndex(h => h === 'toremail');
    if (torEmailIndex !== -1) {
      columnIndices['torEmail'] = torEmailIndex;
    }

    // Check for optional status column
    const statusIndex = headerLower.findIndex(h => h === 'status');
    if (statusIndex !== -1) {
      columnIndices['status'] = statusIndex;
    }

    // Parse data rows
    const data = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      try {
        const row = parseCSVLine(lines[i]);
        if (row.length === 0) continue; // Skip empty rows

        const record = {};
        expectedColumns.forEach(col => {
          const value = row[columnIndices[col]] || '';
          record[col] = value.trim();
        });

        // Add torEmail if column exists
        if (columnIndices['torEmail'] !== undefined) {
          const torEmailValue = row[columnIndices['torEmail']] || '';
          record['torEmail'] = torEmailValue.trim();
        } else {
          record['torEmail'] = ''; // Default empty value when column doesn't exist
        }

        // Add status if column exists
        if (columnIndices['status'] !== undefined) {
          const statusValue = row[columnIndices['status']] || '';
          record['status'] = statusValue.trim();
        } else {
          record['status'] = ''; // Default empty value when column doesn't exist
        }

        // Basic validation
        if (!record.email) {
          errors.push(`Row ${i + 1}: Email is required`);
        } else if (!record.email.includes('@')) {
          errors.push(`Row ${i + 1}: Invalid email format`);
        }

        if (!record.firstName) {
          errors.push(`Row ${i + 1}: First name is required`);
        }

        if (!record.lastName) {
          errors.push(`Row ${i + 1}: Last name is required`);
        }

        data.push(record);
      } catch (err) {
        errors.push(`Row ${i + 1}: ${err.message}`);
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation errors:\n${errors.join('\n')}`);
    }

    return data;
  };

  // Function to parse a single CSV line, handling quoted fields and commas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < line.length) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i += 2;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
          i++;
        }
      } else if (char === ',' && !inQuotes) {
        // Field separator
        result.push(current);
        current = '';
        i++;
      } else {
        current += char;
        i++;
      }
    }

    // Add the last field
    result.push(current);
    return result;
  };

  // Function to validate CSV and check existing accounts
  const validateCSV = async () => {
    setLoading(true);
    setError('');
    setValidationResults(null);
    setDryRunResults(null);
    setActiveTab('validation');
    setActiveDryRunFilter('all');

    try {
      // Parse CSV
      const parsedData = parseCSV(csvText);
      
      if (parsedData.length === 0) {
        throw new Error('No valid data rows found');
      }

      // Extract emails for validation
      const emails = parsedData.map(row => row.email).filter(email => email);

      if (emails.length === 0) {
        throw new Error('No valid emails found in the data');
      }

      // Check for duplicate emails within the CSV data
      const emailCounts = {};
      const duplicateEmails = [];
      
      emails.forEach((email, index) => {
        const lowerEmail = email.toLowerCase();
        if (emailCounts[lowerEmail]) {
          emailCounts[lowerEmail].push(index + 2); // +2 because index is 0-based and we skip header row
        } else {
          emailCounts[lowerEmail] = [index + 2];
        }
      });

      // Find duplicates
      Object.keys(emailCounts).forEach(email => {
        if (emailCounts[email].length > 1) {
          duplicateEmails.push(`${email} (appears in rows: ${emailCounts[email].join(', ')})`);
        }
      });

      if (duplicateEmails.length > 0) {
        throw new Error(`Duplicate emails found in CSV:\n${duplicateEmails.join('\n')}`);
      }

      // Extract TOR emails for mapping (only if torEmail column exists and has values)
      const torEmails = parsedData.map(row => row.torEmail).filter(email => email);
      
      // Check existing accounts
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/user-management/check-emails', 
        { emails }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Create mapping from account_id to account details for TOR lookups
      const accountIdMapping = {};
      if (response.data.existingAccounts) {
        response.data.existingAccounts.forEach(account => {
          accountIdMapping[account.account_id] = {
            first_name: account.first_name,
            last_name: account.last_name,
            primary_email: account.primary_email
          };
        });
      }
      setAccountIdToDetailsMapping(accountIdMapping);

      // Fetch TOR email to account ID mapping if TOR emails exist
      let torMapping = {};
      if (torEmails.length > 0) {
        try {
          const torResponse = await axios.post('/api/user-management/check-emails', 
            { emails: torEmails }, 
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Create mapping of TOR email to account ID
          torMapping = {};
          if (torResponse.data.existingAccounts) {
            torResponse.data.existingAccounts.forEach(account => {
              torMapping[account.primary_email.toLowerCase()] = account.account_id;
              // Also add to account ID mapping for TOR accounts
              accountIdMapping[account.account_id] = {
                first_name: account.first_name,
                last_name: account.last_name,
                primary_email: account.primary_email
              };
            });
          }
          setTorEmailMapping(torMapping);
          setAccountIdToDetailsMapping(accountIdMapping);
        } catch (err) {
          console.warn('Failed to fetch TOR email mappings:', err);
          // Continue without TOR mapping if it fails
        }
      } else {
        // Clear TOR mapping if no TOR emails found
        setTorEmailMapping({});
      }

      setValidationResults({
        parsedData,
        emailCheck: response.data,
        totalRows: parsedData.length
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to check if an account has active enrollments
  const hasActiveEnrollment = (account) => {
    if (!account.accesscards || !Array.isArray(account.accesscards)) return false;
    
    const today = new Date();
    
    for (const card of account.accesscards) {
      if (card.enrollments && Array.isArray(card.enrollments)) {
        for (const enrollment of card.enrollments) {
          if (enrollment.start_date) {
            const start = new Date(enrollment.start_date);
            if (!enrollment.end_date) {
              // No end date means it's still active if start date has passed
              if (start <= today) return true;
            } else {
              const end = new Date(enrollment.end_date);
              if (start <= today && today <= end) return true;
            }
          }
        }
      }
    }
    return false;
  };

  // Function to get active enrollment details
  const getActiveEnrollmentDetails = (account) => {
    if (!account.accesscards || !Array.isArray(account.accesscards)) return null;
    
    const today = new Date();
    let activeEnrollment = null;
    
    for (const card of account.accesscards) {
      if (card.enrollments && Array.isArray(card.enrollments)) {
        for (const enrollment of card.enrollments) {
          if (enrollment.start_date) {
            const start = new Date(enrollment.start_date);
            if (!enrollment.end_date) {
              if (start <= today) {
                activeEnrollment = { ...enrollment, accessCard: card };
                break;
              }
            } else {
              const end = new Date(enrollment.end_date);
              if (start <= today && today <= end) {
                activeEnrollment = { ...enrollment, accessCard: card };
                break;
              }
            }
          }
        }
      }
      if (activeEnrollment) break;
    }
    
    return activeEnrollment;
  };

  // Function to generate UUID (simple implementation for demo)
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Function to perform dry run analysis
  const performDryRun = () => {
    if (!validationResults) {
      setError('Please validate CSV data first');
      return;
    }

    const dryRunData = [];
    const errors = [];

    // Process existing accounts
    if (validationResults.emailCheck.existingAccounts) {
      validationResults.emailCheck.existingAccounts.forEach(account => {
        const activeEnrollment = getActiveEnrollmentDetails(account);
        const hasActiveEnrollment = !!activeEnrollment;

        // Find matching CSV row by email
        const csvRow = validationResults.parsedData.find(row => 
          row.email.toLowerCase() === account.primary_email.toLowerCase()
        );

        if (!csvRow) {
          errors.push(`No CSV row found for account: ${account.primary_email}`);
          return;
        }

        // Check if updates are actually needed
        const needsPasswordUpdate = !updateFilters.ignorePassword && csvRow.password;
        const needsFirstNameUpdate = !updateFilters.ignoreFirstName && csvRow.firstName !== account.first_name;
        const needsLastNameUpdate = !updateFilters.ignoreLastName && csvRow.lastName !== account.last_name;
        
        let needsAssociationUpdate = false;
        let needsConcentrationUpdate = false;
        let needsGradeLevelUpdate = false;
        let needsTorUpdate = false;
        let torProposedAlreadyLinked = false;
        let needsInactiveUpdate = false;
        
        // Find target access card (same logic as enrollment) - needed for inactive status check and display
        let targetAccessCard = null;
        if (!hasActiveEnrollment && account.accesscards && account.accesscards.length > 0) {
          targetAccessCard = account.accesscards[0];
        } else if (hasActiveEnrollment) {
          targetAccessCard = activeEnrollment.accessCard;
        }
        
        // Check inactive status update
        if (csvRow.status && !updateFilters.ignoreInactive && targetAccessCard) {
          const csvStatus = csvRow.status.toLowerCase().trim();
          const isCsvActive = csvStatus === 'active' || csvStatus === '1' || csvStatus === 'true';
          const isCsvInactive = csvStatus === 'inactive' || csvStatus === '0' || csvStatus === 'false';
          
          const currentInactive = targetAccessCard.inactive === true;
          if (isCsvInactive && !currentInactive) {
            needsInactiveUpdate = true;
          } else if (isCsvActive && currentInactive) {
            needsInactiveUpdate = true;
          }
        }
        
        if (hasActiveEnrollment) {
          const currentAssociation = activeEnrollment.association_title || 'None';
          const currentConcentration = activeEnrollment.concentration_title || 'None';
          const currentGradeLevel = activeEnrollment.gradelevel || 'None';
          
          if (csvRow.faCode) {
            const association = associations.find(a => a.title === csvRow.faCode);
            needsAssociationUpdate = !updateFilters.ignoreAssociation && association && currentAssociation !== association.title;
          }
          
          if (csvRow.concentration) {
            const concentration = concentrations.find(c => c.title === csvRow.concentration);
            needsConcentrationUpdate = !updateFilters.ignoreConcentration && concentration && currentConcentration !== concentration.title;
          }
          
          needsGradeLevelUpdate = !updateFilters.ignoreGradeLevel && csvRow.gradeLevel && currentGradeLevel !== csvRow.gradeLevel;
          
          // TOR: add TEACHER_OF_RECORD only if torEmail resolves to an account not already linked on this enrollment
          if (csvRow.torEmail) {
            const proposedTorId = torEmailMapping[csvRow.torEmail.toLowerCase()] || null;
            const linkedIds = torAccountIdsFromEnrollment(activeEnrollment);
            torProposedAlreadyLinked = !!(proposedTorId && linkedIds.has(String(proposedTorId)));
            needsTorUpdate =
              !updateFilters.ignoreTor && !!proposedTorId && !linkedIds.has(String(proposedTorId));
          }
        } else {
          // No active enrollment: creating/linking first enrollment — TOR row uses sync on server when email resolves
          needsAssociationUpdate = !updateFilters.ignoreAssociation && csvRow.faCode;
          needsConcentrationUpdate = !updateFilters.ignoreConcentration && csvRow.concentration;
          needsGradeLevelUpdate = !updateFilters.ignoreGradeLevel && csvRow.gradeLevel;
          const propTor = csvRow.torEmail ? torEmailMapping[csvRow.torEmail.toLowerCase()] : null;
          needsTorUpdate = !updateFilters.ignoreTor && !!propTor;
        }

        const hasAnyUpdates = needsPasswordUpdate || needsFirstNameUpdate || needsLastNameUpdate || 
                             needsAssociationUpdate || needsConcentrationUpdate || needsGradeLevelUpdate || needsTorUpdate || needsInactiveUpdate;
        
        // Determine what specific updates are needed
        const needsAccountUpdate = needsPasswordUpdate || needsFirstNameUpdate || needsLastNameUpdate;
        const needsEnrollmentUpdate = needsAssociationUpdate || needsConcentrationUpdate || needsGradeLevelUpdate || needsTorUpdate;
        const needsAccessCardUpdate = needsInactiveUpdate;

        const changes = {
          account: {
            email: account.primary_email,
            name: `${account.first_name} ${account.last_name}`,
            currentPassword: '***hidden***',
            newPassword: needsPasswordUpdate ? '***will be updated***' : 'No change',
            needsFirstNameUpdate,
            needsLastNameUpdate,
            currentFirstName: account.first_name,
            currentLastName: account.last_name,
            proposedFirstName: csvRow.firstName,
            proposedLastName: csvRow.lastName
          },
          accessCard: {
            needsInactiveUpdate,
            currentInactive: targetAccessCard ? (targetAccessCard.inactive === true) : null,
            proposedInactive: csvRow.status ? (csvRow.status.toLowerCase().trim() === 'inactive' || csvRow.status.toLowerCase().trim() === '0' || csvRow.status.toLowerCase().trim() === 'false') : null,
            csvStatus: csvRow.status || 'Not specified'
          },
          tor: {
            email: csvRow.torEmail || 'Not specified',
            accountId: csvRow.torEmail ? torEmailMapping[csvRow.torEmail.toLowerCase()] : null,
            found: csvRow.torEmail ? !!torEmailMapping[csvRow.torEmail.toLowerCase()] : false,
            proposedAlreadyLinked: torProposedAlreadyLinked
          },
          enrollment: {
            hasActiveEnrollment,
            current: hasActiveEnrollment ? {
              association: activeEnrollment.association_title || 'None',
              concentration: activeEnrollment.concentration_title || 'None',
              gradelevel: activeEnrollment.gradelevel || 'None',
              tor: formatEnrollmentTorDisplay(activeEnrollment, accountIdToDetailsMapping)
            } : {
              association: 'No active enrollment',
              concentration: 'No active enrollment',
              gradelevel: 'No active enrollment',
              tor: 'No active enrollment'
            },
            proposed: {
              association: null,
              concentration: null,
              gradelevel: csvRow.gradeLevel || 'No change'
            },
            action: hasActiveEnrollment ? 'UPDATE' : 'CREATE',
            needsAssociationUpdate,
            needsConcentrationUpdate,
            needsGradeLevelUpdate,
            needsTorUpdate,
            hasAnyUpdates,
            needsAccountUpdate,
            needsEnrollmentUpdate,
            needsAccessCardUpdate
          }
        };

        // Find association by faCode
        if (csvRow.faCode) {
          const association = associations.find(a => a.title === csvRow.faCode);
          if (association) {
            changes.enrollment.proposed.association = association.title;
          } else {
            errors.push(`Association not found for faCode: ${csvRow.faCode} (${account.primary_email})`);
            changes.enrollment.proposed.association = `ERROR: "${csvRow.faCode}" not found`;
          }
        } else {
          changes.enrollment.proposed.association = hasActiveEnrollment ? 'No change' : 'Not specified';
        }

        // Find concentration by concentration name
        if (csvRow.concentration) {
          const concentration = concentrations.find(c => c.title === csvRow.concentration);
          if (concentration) {
            changes.enrollment.proposed.concentration = concentration.title;
          } else {
            errors.push(`Concentration not found for: ${csvRow.concentration} (${account.primary_email})`);
            changes.enrollment.proposed.concentration = `ERROR: "${csvRow.concentration}" not found`;
          }
        } else {
          changes.enrollment.proposed.concentration = hasActiveEnrollment ? 'No change' : 'Not specified';
        }

        dryRunData.push({
          type: 'EXISTING',
          account,
          activeEnrollment,
          targetAccessCard,
          csvRow,
          changes
        });
      });
    }

    // Process new accounts
    if (validationResults.emailCheck.newEmails) {
      validationResults.emailCheck.newEmails.forEach(newEmail => {
        // Find matching CSV row by email
        const csvRow = validationResults.parsedData.find(row => 
          row.email.toLowerCase() === newEmail.toLowerCase()
        );

        if (!csvRow) {
          errors.push(`No CSV row found for new email: ${newEmail}`);
          return;
        }

        // Generate UUIDs for new account and access card
        const accountUUID = generateUUID();
        const accessCardUUID = generateUUID();

        const changes = {
          account: {
            email: csvRow.email,
            name: `${csvRow.firstName} ${csvRow.lastName}`,
            newPassword: csvRow.password ? '***will be created***' : 'ERROR: Password required'
          },
          accessCard: {
            email: csvRow.email,
            role: 'Student',
            schoolId: CSV_IMPORT_SCHOOL_ID,
            inactive: csvRow.status ? (csvRow.status.toLowerCase().trim() === 'inactive' || csvRow.status.toLowerCase().trim() === '0' || csvRow.status.toLowerCase().trim() === 'false') : false
          },
          tor: {
            email: csvRow.torEmail || 'Not specified',
            accountId: csvRow.torEmail ? torEmailMapping[csvRow.torEmail.toLowerCase()] : null,
            found: csvRow.torEmail ? !!torEmailMapping[csvRow.torEmail.toLowerCase()] : false
          },
          enrollment: {
            action: 'CREATE_NEW',
            proposed: {
              association: null,
              concentration: null,
              gradelevel: csvRow.gradeLevel || 'Not specified'
            }
          }
        };

        // Find association by faCode
        if (csvRow.faCode) {
          const association = associations.find(a => a.title === csvRow.faCode);
          if (association) {
            changes.enrollment.proposed.association = association.title;
          } else {
            errors.push(`Association not found for faCode: ${csvRow.faCode} (${csvRow.email})`);
            changes.enrollment.proposed.association = `ERROR: "${csvRow.faCode}" not found`;
          }
        } else {
          changes.enrollment.proposed.association = 'Not specified';
        }

        // Find concentration by concentration name
        if (csvRow.concentration) {
          const concentration = concentrations.find(c => c.title === csvRow.concentration);
          if (concentration) {
            changes.enrollment.proposed.concentration = concentration.title;
          } else {
            errors.push(`Concentration not found for: ${csvRow.concentration} (${csvRow.email})`);
            changes.enrollment.proposed.concentration = `ERROR: "${csvRow.concentration}" not found`;
          }
        } else {
          changes.enrollment.proposed.concentration = 'Not specified';
        }

        dryRunData.push({
          type: 'NEW',
          accountUUID,
          accessCardUUID,
          csvRow,
          changes
        });
      });
    }

    // Calculate detailed counts
    const newAccounts = dryRunData.filter(item => item.type === 'NEW');
    const existingAccounts = dryRunData.filter(item => item.type === 'EXISTING');
    
    const accountsToInsert = newAccounts.length;
    const accountsToUpdate = existingAccounts.filter(item => item.changes.enrollment.hasAnyUpdates).length;
    
    const accessCardsToInsert = newAccounts.length; // Each new account gets a new access card
    const accessCardsToUpdate = 0; // We don't update access cards in this flow
    
    const enrollmentsToInsert = newAccounts.length + existingAccounts.filter(item => !item.changes.enrollment.hasActiveEnrollment).length;
    const enrollmentsToUpdate = existingAccounts.filter(item => 
      item.changes.enrollment.hasActiveEnrollment && item.changes.enrollment.needsEnrollmentUpdate
    ).length;

    setDryRunResults({
      accountsToUpdate: dryRunData,
      errors,
      totalAccounts: dryRunData.length,
      counts: {
        accountsToInsert,
        accountsToUpdate,
        accessCardsToInsert,
        accessCardsToUpdate,
        enrollmentsToInsert,
        enrollmentsToUpdate
      }
    });
  };

  // Function to execute batch import
  const executeBatchImport = async () => {
    if (!validationResults || !dryRunResults) {
      setError('Please validate CSV data and perform dry run first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare data for batch import
      const existingAccounts = dryRunResults.accountsToUpdate
        .filter(item => item.type === 'EXISTING')
        .map(item => ({
          account: item.account,
          csvRow: item.csvRow
        }));

      const newAccounts = dryRunResults.accountsToUpdate
        .filter(item => item.type === 'NEW')
        .map(item => ({
          csvRow: item.csvRow
        }));

      // Check if payload is too large and use chunked processing if needed
      const payload = {
        existingAccounts,
        newAccounts,
        associations,
        concentrations,
        torEmailMapping,
        batchSize: 50,
        updateFilters
      };

      const payloadSize = JSON.stringify(payload).length;
      const maxPayloadSize = 10 * 1024 * 1024; // 10MB limit

      if (payloadSize > maxPayloadSize) {
        // Use chunked processing for large payloads
        await executeChunkedBatchImport(payload, token);
      } else {
        // Use single request for smaller payloads
        const response = await axios.post('/api/user-management/batch-import', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          const updated = response.data.details?.existingAccounts?.updated || 0;
          const created = response.data.details?.newAccounts?.created || 0;
          const processed = updated + created;
          const message = `Batch import completed successfully!\n\nProcessed: ${processed} accounts\nExisting accounts updated: ${updated}\nNew accounts created: ${created}`;
          setLoading(false);
          setImportResult({ type: 'success', message });
        } else {
          const errorCount = response.data.errors?.length || 0;
          const updated = response.data.details?.existingAccounts?.updated || 0;
          const created = response.data.details?.newAccounts?.created || 0;
          const processed = updated + created;
          const errorDetails = (response.data.errors || []).slice(0, 5).map(err => `• ${err.email}: ${err.error}`).join('\n');
          const moreErrors = errorCount > 5 ? `\n... and ${errorCount - 5} more errors` : '';
          const message = `Batch import completed with errors!\n\nProcessed: ${processed} accounts\nExisting accounts updated: ${updated}\nNew accounts created: ${created}\nErrors: ${errorCount}\n\nFirst few errors:\n${errorDetails}${moreErrors}`;
          setLoading(false);
          setImportResult({ type: 'error', message });
        }
      }

    } catch (err) {
      console.error('Batch import error:', err);
      const errorMessage = `Batch import failed: ${err.response?.data?.error || err.message}`;
      setLoading(false);
      setImportResult({ type: 'error', message: errorMessage });
      setError(errorMessage);
    }
  };

  // Function to execute chunked batch import for large payloads
  const executeChunkedBatchImport = async (payload, token) => {
    const chunkSize = 100; // Process 100 accounts per chunk
    const { existingAccounts, newAccounts, associations, concentrations, torEmailMapping, updateFilters } = payload;
    
    let totalProcessed = 0;
    let totalErrors = [];
    let totalExistingUpdated = 0;
    let totalNewCreated = 0;

    // Process existing accounts in chunks
    if (existingAccounts && existingAccounts.length > 0) {
      for (let i = 0; i < existingAccounts.length; i += chunkSize) {
        const chunk = existingAccounts.slice(i, i + chunkSize);
        const chunkPayload = {
          existingAccounts: chunk,
          newAccounts: [],
          associations,
          concentrations,
          torEmailMapping,
          batchSize: 50,
          updateFilters
        };

        try {
          const response = await axios.post('/api/user-management/batch-import', chunkPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.success) {
            totalProcessed += response.data.processed;
            totalExistingUpdated += response.data.details.existingAccounts.updated;
            totalErrors.push(...response.data.errors);
          } else {
            totalErrors.push(...response.data.errors);
          }
        } catch (err) {
          totalErrors.push({
            email: 'Chunk processing error',
            error: err.response?.data?.error || err.message
          });
        }
      }
    }

    // Process new accounts in chunks
    if (newAccounts && newAccounts.length > 0) {
      for (let i = 0; i < newAccounts.length; i += chunkSize) {
        const chunk = newAccounts.slice(i, i + chunkSize);
        const chunkPayload = {
          existingAccounts: [],
          newAccounts: chunk,
          associations,
          concentrations,
          torEmailMapping,
          batchSize: 50,
          updateFilters
        };

        try {
          const response = await axios.post('/api/user-management/batch-import', chunkPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data.success) {
            totalProcessed += response.data.processed;
            totalNewCreated += response.data.details.newAccounts.created;
            totalErrors.push(...response.data.errors);
          } else {
            totalErrors.push(...response.data.errors);
          }
        } catch (err) {
          totalErrors.push({
            email: 'Chunk processing error',
            error: err.response?.data?.error || err.message
          });
        }
      }
    }

    // Show results
    const totalProcessedActual = totalExistingUpdated + totalNewCreated;
    if (totalErrors.length === 0) {
      const message = `Chunked batch import completed successfully!\n\nProcessed: ${totalProcessedActual} accounts\nExisting accounts updated: ${totalExistingUpdated}\nNew accounts created: ${totalNewCreated}`;
      setLoading(false);
      setImportResult({ type: 'success', message });
    } else {
      const errorCount = totalErrors.length;
      const errorDetails = totalErrors.slice(0, 5).map(err => `• ${err.email}: ${err.error}`).join('\n');
      const moreErrors = errorCount > 5 ? `\n... and ${errorCount - 5} more errors` : '';
      const message = `Chunked batch import completed with errors!\n\nProcessed: ${totalProcessedActual} accounts\nExisting accounts updated: ${totalExistingUpdated}\nNew accounts created: ${totalNewCreated}\nErrors: ${errorCount}\n\nFirst few errors:\n${errorDetails}${moreErrors}`;
      setLoading(false);
      setImportResult({ type: 'error', message });
    }
  };

  // Function to clear all data
  const clearData = () => {
    setCsvText('');
    setValidationResults(null);
    setError('');
    setDryRunResults(null);
    setActiveTab('validation');
    setActiveValidationSection('existing');
    setActiveDryRunFilter('all');
    setImportResult(null);
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <h1 style={styles.title}>CSV Import - Users</h1>
          <span style={styles.schoolBadge}>
            🏫 {CSV_IMPORT_SCHOOL_NAME}
          </span>
        </div>
        <p style={styles.subtitle}>
          Paste your CSV data below to validate and check for existing accounts.
          <br />
          <strong>Note:</strong> This import is configured specifically for {CSV_IMPORT_SCHOOL_NAME} students only.
          <br />
          Required columns: {expectedColumns.join(', ')}
          <br />
          Optional columns: torEmail, status
          <br />
          <strong>torEmail:</strong> one account email per row. If that account is not already a Teacher of Record for the student’s active enrollment, the import <strong>adds</strong> a TEACHER_OF_RECORD relationship (co‑TORs supported). If that teacher is already linked, no relationship change is made for TOR.
        </p>
      </div>

      <div style={styles.inputSection}>
        <div style={styles.textareaContainer}>
          <div style={styles.labelContainer}>
            <label style={styles.label}>CSV Data:</label>
            <button
              onClick={clearData}
              style={styles.clearButton}
              title="Clear Textbox and all Results"
            >
              Clear Textbox and all Results
            </button>
          </div>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`Paste your CSV data here.\n\ntorEmail = optional, ONE email per row (not comma-separated).\n\nExample:\n${expectedColumns.join(',')},torEmail\nSmith,John,12,FA001,john.smith@email.com,password123,Computer Science,teacher@school.com`}
            style={styles.textarea}
            rows={10}
          />
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={validateCSV}
            disabled={loading || !csvText.trim()}
            style={{
              ...styles.button,
              ...styles.primaryButton,
              ...(loading || !csvText.trim() ? styles.disabledButton : {})
            }}
          >
            {loading ? 'Validating...' : 'Validate CSV'}
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.errorContainer}>
          <h3 style={styles.errorTitle}>Validation Error</h3>
          <pre style={styles.errorText}>{error}</pre>
        </div>
      )}

      {(validationResults || dryRunResults) && (
        <div style={styles.tabsContainer}>
          <div style={styles.tabsHeader}>
            <button
              onClick={() => setActiveTab('validation')}
              style={{
                ...styles.tabButton,
                ...(activeTab === 'validation' ? styles.activeTabButton : {})
              }}
            >
              Data
            </button>
            {dryRunResults && (
              <button
                onClick={() => setActiveTab('dryRun')}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === 'dryRun' ? styles.activeTabButton : {})
                }}
              >
                Dry Run
              </button>
            )}
          </div>

          {activeTab === 'validation' && validationResults && (
            <div style={styles.resultsContainer}>
              <div style={styles.resultsHeader}>
                <h2 style={styles.resultsTitle}>Validation Results</h2>
                {validationResults.emailCheck.existingAccounts.length > 0 && (
                  <button
                    onClick={() => {
                      performDryRun();
                      setActiveTab('dryRun');
                    }}
                    style={{ ...styles.button, ...styles.dryRunButton }}
                  >
                    Start Dry Run
                  </button>
                )}
              </div>
              
              <div style={styles.summaryContainer}>
                <div style={styles.summaryCard}>
                  <h3 style={styles.summaryTitle}>Total Rows</h3>
                  <div style={styles.summaryNumber}>{validationResults.totalRows}</div>
                </div>
                
                <div style={styles.summaryCard}>
                  <h3 style={styles.summaryTitle}>Existing Accounts</h3>
                  <div style={{ ...styles.summaryNumber, color: '#6c757d' }}>
                    {validationResults.emailCheck.existingCount}
                  </div>
                </div>
                
                <div style={styles.summaryCard}>
                  <h3 style={styles.summaryTitle}>New Accounts</h3>
                  <div style={{ ...styles.summaryNumber, color: '#495057' }}>
                    {validationResults.emailCheck.newCount}
                  </div>
                </div>
              </div>

              {/* Validation Section Bookmarks */}
              {(validationResults.emailCheck.existingAccounts.length > 0 || validationResults.emailCheck.newEmails.length > 0) && (
                <div style={styles.validationBookmarks}>
                  {validationResults.emailCheck.existingAccounts.length > 0 && (
                    <button
                      onClick={() => setActiveValidationSection('existing')}
                      style={{
                        ...styles.bookmarkButton,
                        ...(activeValidationSection === 'existing' ? styles.activeBookmarkButton : {})
                      }}
                    >
                      Existing Accounts ({validationResults.emailCheck.existingAccounts.length})
                    </button>
                  )}
                  {validationResults.emailCheck.newEmails.length > 0 && (
                    <button
                      onClick={() => setActiveValidationSection('new')}
                      style={{
                        ...styles.bookmarkButton,
                        ...(activeValidationSection === 'new' ? styles.activeBookmarkButton : {})
                      }}
                    >
                      New Accounts ({validationResults.emailCheck.newEmails.length})
                    </button>
                  )}
                </div>
              )}

              {activeValidationSection === 'existing' && validationResults.emailCheck.existingAccounts.length > 0 && (
                <div style={styles.existingContainer}>
                  <h3 style={styles.sectionTitle}>Existing Accounts ({validationResults.emailCheck.existingAccounts.length})</h3>
                  <div style={styles.accountsContainer}>
                    {validationResults.emailCheck.existingAccounts.map((account, index) => {
                  const hasActive = hasActiveEnrollment(account);
                  const activeEnrollment = getActiveEnrollmentDetails(account);
                  
                  // Find matching CSV row
                  const csvRow = validationResults.parsedData.find(row => 
                    row.email.toLowerCase() === account.primary_email.toLowerCase()
                  );
                  
                  // Check status mismatch
                  let statusWarning = null;
                  if (csvRow && csvRow.status) {
                    const csvStatus = csvRow.status.toLowerCase().trim();
                    const isCsvActive = csvStatus === 'active' || csvStatus === '1' || csvStatus === 'true';
                    const isCsvInactive = csvStatus === 'inactive' || csvStatus === '0' || csvStatus === 'false';
                    
                    // Check all access cards for inactive status
                    if (account.accesscards && account.accesscards.length > 0) {
                      const allAccessCardsInactive = account.accesscards.every(card => card.inactive === true);
                      const hasActiveAccessCard = account.accesscards.some(card => card.inactive !== true);
                      const accessCardCount = account.accesscards.length;
                      const cardText = accessCardCount === 1 ? 'access card' : 'access cards';
                      
                      if (isCsvInactive && hasActiveAccessCard) {
                        statusWarning = `User is stated as inactive in CSV, but has ${cardText} that are active (inactive not true)`;
                      } else if (isCsvActive && allAccessCardsInactive) {
                        statusWarning = `User in CSV data is marked active, but ${cardText} ${accessCardCount === 1 ? 'is' : 'are'} inactive`;
                      }
                    }
                  }
                  
                  return (
                    <div key={index} style={styles.accountCard}>
                      <div style={styles.accountHeader}>
                        <div style={styles.accountInfo}>
                          <h4 style={styles.accountName}>
                            {account.first_name} {account.last_name}
                          </h4>
                          <p style={styles.accountEmail}>{account.primary_email}</p>
                          <p style={styles.accountId}>ID: {account.account_id}</p>
                        </div>
                        <div style={styles.enrollmentStatus}>
                          {hasActive ? (
                            <div style={styles.activeEnrollmentBadge}>
                              <span style={styles.activeText}>✓ Active Enrollment</span>
                            </div>
                          ) : (
                            <div style={styles.inactiveEnrollmentBadge}>
                              <span style={styles.inactiveText}>✗ No Active Enrollment</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {statusWarning && (
                        <div style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem',
                          backgroundColor: '#fff3cd',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          fontSize: '0.8125rem',
                          color: '#856404'
                        }}>
                          <strong>⚠ Status Mismatch:</strong> {statusWarning}
                        </div>
                      )}
                      
                      {hasActive && activeEnrollment && (
                        <div style={styles.enrollmentDetails}>
                          <h5 style={styles.enrollmentTitle}>Active Enrollment Details:</h5>
                          <div style={styles.enrollmentInfo}>
                            <div style={styles.enrollmentRow}>
                              <strong>School:</strong> {activeEnrollment.accessCard.school_name || 'Global'}
                            </div>
                            <div style={styles.enrollmentRow}>
                              <strong>Role:</strong> {activeEnrollment.accessCard.role}
                            </div>
                            <div style={styles.enrollmentRow}>
                              <strong>Start Date:</strong> {activeEnrollment.start_date}
                            </div>
                            {activeEnrollment.end_date && (
                              <div style={styles.enrollmentRow}>
                                <strong>End Date:</strong> {activeEnrollment.end_date}
                              </div>
                            )}
                            {activeEnrollment.gradelevel && (
                              <div style={styles.enrollmentRow}>
                                <strong>Grade Level:</strong> {activeEnrollment.gradelevel}
                              </div>
                            )}
                            {activeEnrollment.concentration_title && (
                              <div style={styles.enrollmentRow}>
                                <strong>Concentration:</strong> {activeEnrollment.concentration_title}
                              </div>
                            )}
                            {activeEnrollment.association_title && (
                              <div style={styles.enrollmentRow}>
                                <strong>Association:</strong> {activeEnrollment.association_title}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                    })}
                  </div>
                </div>
              )}

              {activeValidationSection === 'new' && validationResults.emailCheck.newEmails.length > 0 && (
                <div style={styles.newContainer}>
                  <h3 style={styles.sectionTitle}>New Accounts ({validationResults.emailCheck.newEmails.length})</h3>
                  <div style={styles.tableContainer}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Email</th>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Grade Level</th>
                          <th style={styles.th}>FA Code</th>
                          <th style={styles.th}>Concentration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResults.parsedData
                          .filter(row => validationResults.emailCheck.newEmails.includes(row.email.toLowerCase()))
                          .map((row, index) => (
                            <tr key={index} style={styles.tr}>
                              <td style={styles.td}>{row.email}</td>
                              <td style={styles.td}>{row.firstName} {row.lastName}</td>
                              <td style={styles.td}>{row.gradeLevel}</td>
                              <td style={styles.td}>{row.faCode}</td>
                              <td style={styles.td}>{row.concentration}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'dryRun' && dryRunResults && (
            <div style={styles.dryRunContainer}>
              <div style={styles.dryRunHeader}>
                <h2 style={styles.dryRunTitle}>Dry Run Results</h2>
                <button
                  onClick={executeBatchImport}
                  disabled={loading}
                  style={{ 
                    ...styles.button, 
                    ...styles.dryRunButton,
                    ...(loading ? styles.disabledButton : {})
                  }}
                >
                  {loading ? 'Executing...' : 'Execute Import'}
                </button>
              </div>

              {/* Dry Run Filter Bookmarks */}
              <div style={styles.dryRunBookmarks}>
                <button
                  onClick={() => setActiveDryRunFilter('all')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'all' ? styles.activeBookmarkButton : {})
                  }}
                >
                  All ({dryRunResults.accountsToUpdate.length})
                </button>
                <button
                  onClick={() => setActiveDryRunFilter('new')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'new' ? styles.activeBookmarkButton : {})
                  }}
                >
                  New Accounts ({dryRunResults.accountsToUpdate.filter(item => item.type === 'NEW').length})
                </button>
                <button
                  onClick={() => setActiveDryRunFilter('accountUpdate')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'accountUpdate' ? styles.activeBookmarkButton : {})
                  }}
                >
                  Account Updates ({dryRunResults.accountsToUpdate.filter(item => item.type === 'EXISTING' && item.changes.enrollment.needsAccountUpdate).length})
                </button>
                <button
                  onClick={() => setActiveDryRunFilter('enrollmentUpdate')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'enrollmentUpdate' ? styles.activeBookmarkButton : {})
                  }}
                >
                  Enrollment Updates ({dryRunResults.accountsToUpdate.filter(item => item.type === 'EXISTING' && item.changes.enrollment.needsEnrollmentUpdate).length})
                </button>
                <button
                  onClick={() => setActiveDryRunFilter('accessCardUpdate')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'accessCardUpdate' ? styles.activeBookmarkButton : {})
                  }}
                >
                  Access Card Updates ({dryRunResults.accountsToUpdate.filter(item => item.type === 'EXISTING' && item.changes.enrollment.needsAccessCardUpdate).length})
                </button>
                <button
                  onClick={() => setActiveDryRunFilter('noUpdate')}
                  style={{
                    ...styles.bookmarkButton,
                    ...(activeDryRunFilter === 'noUpdate' ? styles.activeBookmarkButton : {})
                  }}
                >
                  No Updates ({dryRunResults.accountsToUpdate.filter(item => item.type === 'EXISTING' && !item.changes.enrollment.hasAnyUpdates).length})
                </button>
              </div>
          
          {/* Update Filters */}
          <div style={styles.filterContainer}>
            <h3 style={styles.filterTitle}>Update Filters:</h3>
            <div style={styles.filterGrid}>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignorePassword}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignorePassword: e.target.checked})}
                />
                Ignore Password Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreFirstName}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreFirstName: e.target.checked})}
                />
                Ignore First Name Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreLastName}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreLastName: e.target.checked})}
                />
                Ignore Last Name Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreAssociation}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreAssociation: e.target.checked})}
                />
                Ignore Association Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreConcentration}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreConcentration: e.target.checked})}
                />
                Ignore Concentration Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreGradeLevel}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreGradeLevel: e.target.checked})}
                />
                Ignore Grade Level Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreTor}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreTor: e.target.checked})}
                />
                Ignore Teacher of Record Updates
              </label>
              <label style={styles.filterLabel}>
                <input
                  type="checkbox"
                  checked={updateFilters.ignoreInactive}
                  onChange={(e) => setUpdateFilters({...updateFilters, ignoreInactive: e.target.checked})}
                />
                Ignore Inactive Mismatch Updates
              </label>
            </div>
            <button
              onClick={performDryRun}
              style={styles.refreshButton}
              title="Dry Rerun"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.65 2.35C12.2 0.9 10.2 0 8 0 3.58 0 0 3.58 0 8s3.58 8 8 8c2.47 0 4.73-1.1 6.24-2.85l-1.5-1.5C11.76 13.5 9.95 14.5 8 14.5c-3.59 0-6.5-2.91-6.5-6.5S4.41 1.5 8 1.5c1.66 0 3.16.63 4.3 1.65L10 5h5V0l-1.35 2.35z" fill="currentColor"/>
              </svg>
            </button>
          </div>
          
          {dryRunResults.errors.length > 0 && (
            <div style={styles.dryRunErrors}>
              <h3 style={styles.dryRunErrorTitle}>Errors Found:</h3>
              <ul style={styles.dryRunErrorList}>
                {dryRunResults.errors.map((error, index) => (
                  <li key={index} style={styles.dryRunErrorItem}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={styles.dryRunSummary}>
            <h3>Summary of Changes:</h3>
            <div style={styles.dryRunCounts}>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.accountsToInsert}</strong> accounts to insert
              </div>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.accountsToUpdate}</strong> accounts to update
              </div>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.accessCardsToInsert}</strong> access cards to insert
              </div>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.accessCardsToUpdate}</strong> access cards to update
              </div>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.enrollmentsToInsert}</strong> enrollment items to insert
              </div>
              <div style={styles.dryRunCountItem}>
                <strong>{dryRunResults.counts.enrollmentsToUpdate}</strong> enrollment items to update
              </div>
            </div>
          </div>

          <div style={styles.dryRunAccounts}>
            {dryRunResults.accountsToUpdate
              .filter(item => {
                if (activeDryRunFilter === 'all') return true;
                if (activeDryRunFilter === 'new') return item.type === 'NEW';
                if (activeDryRunFilter === 'accountUpdate') return item.type === 'EXISTING' && item.changes.enrollment.needsAccountUpdate;
                if (activeDryRunFilter === 'enrollmentUpdate') return item.type === 'EXISTING' && item.changes.enrollment.needsEnrollmentUpdate;
                if (activeDryRunFilter === 'accessCardUpdate') return item.type === 'EXISTING' && item.changes.enrollment.needsAccessCardUpdate;
                if (activeDryRunFilter === 'noUpdate') return item.type === 'EXISTING' && !item.changes.enrollment.hasAnyUpdates;
                return true;
              })
              .map((item, index) => (
              <div key={index} style={styles.dryRunAccountCard}>
                <div style={styles.dryRunAccountHeader}>
                  <div style={styles.dryRunAccountInfo}>
                    <h4 style={styles.dryRunAccountName}>{item.changes.account.name}</h4>
                    <p style={styles.dryRunAccountEmail}>{item.changes.account.email}</p>
                  </div>
                  <div style={styles.dryRunActionBadge}>
                    {item.type === 'NEW' ? (
                      <span style={styles.newAccountBadge}>CREATE NEW ACCOUNT</span>
                    ) : (
                      <>
                        {item.changes.enrollment.needsAccountUpdate && (
                          <span style={styles.accountUpdateBadge}>🔄Account</span>
                        )}
                        {item.changes.enrollment.needsEnrollmentUpdate && (
                          <span style={styles.enrollmentUpdateBadge}>🔄Enrollment</span>
                        )}
                        {item.changes.enrollment.needsAccessCardUpdate && (
                          <span style={styles.accessCardUpdateBadge}>🔄Access Card</span>
                        )}
                        {!item.changes.enrollment.hasAnyUpdates && (
                          <span style={styles.noUpdateBadge}>NO UPDATES NEEDED</span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div style={styles.dryRunChanges}>
                  <h5 style={styles.dryRunChangesTitle}>Proposed Changes:</h5>
                  
                  {item.type === 'NEW' ? (
                    <>
                      {/* New Account Creation */}
                      <div style={styles.dryRunChangeSection}>
                        <h6 style={styles.dryRunChangeSubtitle}>New Account Creation:</h6>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Account ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.accountUUID}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Email:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.account.email}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Name:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.account.name}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Password:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.account.newPassword}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Default Access Card ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.accessCardUUID}</span>
                        </div>
                      </div>

                      {/* New Access Card Creation */}
                      <div style={styles.dryRunChangeSection}>
                        <h6 style={styles.dryRunChangeSubtitle}>New Access Card Creation:</h6>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Access Card ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.accessCardUUID}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Email:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.accessCard.email}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Role:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.accessCard.role}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>School ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.accessCard.schoolId}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Account ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.accountUUID}</span>
                        </div>
                        {item.changes.accessCard.inactive !== undefined && (
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Inactive:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.accessCard.inactive ? 'Yes (inactive)' : 'No (active)'}</span>
                          </div>
                        )}
                      </div>

                      {/* New Enrollment Creation */}
                      <div style={styles.dryRunChangeSection}>
                        <h6 style={styles.dryRunChangeSubtitle}>New Enrollment Creation:</h6>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Start Date:</span>
                          <span style={styles.dryRunChangeValue}>2025-07-01</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>End Date:</span>
                          <span style={styles.dryRunChangeValue}>2026-06-30</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Period ID:</span>
                          <span style={styles.dryRunChangeValue}>99f1f5f0-b898-4f50-b175-67f1c42bb155</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Access Card ID:</span>
                          <span style={styles.dryRunChangeValue}>{item.accessCardUUID}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Association:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.association}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Concentration:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.concentration}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Grade Level:</span>
                          <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.gradelevel}</span>
                        </div>
                        <div style={styles.dryRunChangeRow}>
                          <span style={styles.dryRunChangeLabel}>Teacher of Record:</span>
                          <span style={styles.dryRunChangeValue}>
                            {item.changes.tor.email}
                            {item.changes.tor.email !== 'Not specified' && (
                              <span style={{ color: item.changes.tor.found ? '#495057' : '#868e96', marginLeft: '0.5rem' }}>
                                {item.changes.tor.found ? '✓ Found' : '✗ Not Found'}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Existing Account Updates */}
                      {item.changes.account.needsFirstNameUpdate && (
                        <div style={styles.dryRunChangeSection}>
                          <h6 style={styles.dryRunChangeSubtitle}>First Name Update:</h6>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Current:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.currentFirstName}</span>
                          </div>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Proposed:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.proposedFirstName}</span>
                          </div>
                        </div>
                      )}

                      {item.changes.account.needsLastNameUpdate && (
                        <div style={styles.dryRunChangeSection}>
                          <h6 style={styles.dryRunChangeSubtitle}>Last Name Update:</h6>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Current:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.currentLastName}</span>
                          </div>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Proposed:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.proposedLastName}</span>
                          </div>
                        </div>
                      )}

                      {item.changes.account.newPassword !== 'No change' && (
                        <div style={styles.dryRunChangeSection}>
                          <h6 style={styles.dryRunChangeSubtitle}>Password Update:</h6>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Current:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.currentPassword}</span>
                          </div>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>New:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.account.newPassword}</span>
                          </div>
                        </div>
                      )}

                      <div style={styles.dryRunChangeSection}>
                        <h6 style={styles.dryRunChangeSubtitle}>
                          {item.changes.enrollment.action === 'UPDATE' ? 'Enrollment Updates:' : 'New Enrollment Creation:'}
                        </h6>
                        
                        {item.changes.enrollment.action === 'UPDATE' ? (
                          <>
                            {item.changes.enrollment.needsAssociationUpdate && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeLabel}>Association:</span>
                                <span style={styles.dryRunChangeValue}>
                                  {item.changes.enrollment.current.association} → {item.changes.enrollment.proposed.association}
                                </span>
                              </div>
                            )}
                            
                            {item.changes.enrollment.needsConcentrationUpdate && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeLabel}>Concentration:</span>
                                <span style={styles.dryRunChangeValue}>
                                  {item.changes.enrollment.current.concentration} → {item.changes.enrollment.proposed.concentration}
                                </span>
                              </div>
                            )}
                            
                            {item.changes.enrollment.needsGradeLevelUpdate && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeLabel}>Grade Level:</span>
                                <span style={styles.dryRunChangeValue}>
                                  {item.changes.enrollment.current.gradelevel} → {item.changes.enrollment.proposed.gradelevel}
                                </span>
                              </div>
                            )}
                            
                            {item.changes.enrollment.needsTorUpdate && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeLabel}>Teacher of Record:</span>
                                <span style={styles.dryRunChangeValue}>
                                  Add link: {item.changes.tor.email}
                                  {item.changes.tor.found && (
                                    <span style={{ color: '#495057', marginLeft: '0.5rem' }}>✓ Found</span>
                                  )}
                                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#868e96', marginTop: '0.25rem' }}>
                                    (New TEACHER_OF_RECORD relationship; existing TORs on this enrollment are kept.)
                                  </span>
                                </span>
                              </div>
                            )}

                            {item.changes.tor.proposedAlreadyLinked && item.changes.tor.email && item.changes.tor.email !== 'Not specified' && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeLabel}>Teacher of Record:</span>
                                <span style={{ color: '#6c757d' }}>
                                  {item.changes.tor.email} — already linked for this enrollment (no new relationship)
                                </span>
                              </div>
                            )}
                            
                            {!item.changes.enrollment.needsAssociationUpdate && 
                             !item.changes.enrollment.needsConcentrationUpdate && 
                             !item.changes.enrollment.needsGradeLevelUpdate && 
                             !item.changes.enrollment.needsTorUpdate && 
                             !item.changes.tor.proposedAlreadyLinked && (
                              <div style={styles.dryRunChangeRow}>
                                <span style={styles.dryRunChangeValue}>No enrollment updates needed</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Start Date:</span>
                              <span style={styles.dryRunChangeValue}>2025-07-01</span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>End Date:</span>
                              <span style={styles.dryRunChangeValue}>2026-06-30</span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Period ID:</span>
                              <span style={styles.dryRunChangeValue}>99f1f5f0-b898-4f50-b175-67f1c42bb155</span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Access Card ID:</span>
                              <span style={styles.dryRunChangeValue}>
                                {item.targetAccessCard ? item.targetAccessCard.accesscard_id : 'ERROR: No access card found'}
                              </span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Association:</span>
                              <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.association}</span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Concentration:</span>
                              <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.concentration}</span>
                            </div>
                            
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Grade Level:</span>
                              <span style={styles.dryRunChangeValue}>{item.changes.enrollment.proposed.gradelevel}</span>
                            </div>
                            <div style={styles.dryRunChangeRow}>
                              <span style={styles.dryRunChangeLabel}>Teacher of Record:</span>
                              <span style={styles.dryRunChangeValue}>
                                {item.changes.tor.email}
                                {item.changes.tor.email !== 'Not specified' && (
                                  <span style={{ color: item.changes.tor.found ? '#28a745' : '#dc3545', marginLeft: '0.5rem' }}>
                                    {item.changes.tor.found ? '✓ Found' : '✗ Not Found'}
                                  </span>
                                )}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {item.type === 'EXISTING' && item.changes.enrollment.needsAccessCardUpdate && (
                        <div style={styles.dryRunChangeSection}>
                          <h6 style={styles.dryRunChangeSubtitle}>Access Card Status Update:</h6>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>CSV Status:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.accessCard.csvStatus}</span>
                          </div>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Current Inactive:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.accessCard.currentInactive === true ? 'Yes (inactive)' : item.changes.accessCard.currentInactive === false ? 'No (active)' : 'N/A'}</span>
                          </div>
                          <div style={styles.dryRunChangeRow}>
                            <span style={styles.dryRunChangeLabel}>Proposed Inactive:</span>
                            <span style={styles.dryRunChangeValue}>{item.changes.accessCard.proposedInactive === true ? 'Yes (inactive)' : item.changes.accessCard.proposedInactive === false ? 'No (active)' : 'N/A'}</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
            </div>
          )}
        </div>
      )}

      {/* Loading Overlay */}
      {(loading || importResult) && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            {loading && !importResult && (
              <>
                <div style={styles.spinner}></div>
                <p style={styles.loadingText}>Loading...</p>
              </>
            )}
            {importResult && (
              <>
                <div style={{
                  ...styles.resultIcon,
                  backgroundColor: importResult.type === 'success' ? '#d4edda' : '#f8d7da',
                  color: importResult.type === 'success' ? '#155724' : '#721c24'
                }}>
                  {importResult.type === 'success' ? '✓' : '✗'}
                </div>
                <p style={{
                  ...styles.loadingText,
                  color: importResult.type === 'success' ? '#155724' : '#721c24',
                  marginBottom: '0.5rem'
                }}>
                  {importResult.type === 'success' ? 'Import Completed Successfully' : 'Import Completed with Errors'}
                </p>
                <pre style={styles.resultMessage}>{importResult.message}</pre>
                <button
                  onClick={() => {
                    const wasSuccess = importResult.type === 'success';
                    setImportResult(null);
                    setLoading(false);
                    if (wasSuccess) {
                      // Clear all data after successful import
                      clearData();
                    }
                  }}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    marginTop: '1rem'
                  }}
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1.5rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  header: {
    marginBottom: '1.5rem',
    textAlign: 'center'
  },
  schoolBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '0.375rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.8125rem',
    fontWeight: '600',
    border: '1px solid #90caf9',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  title: {
    color: '#2c3e50',
    fontSize: '1.75rem',
    marginBottom: '0.5rem',
    fontWeight: '600'
  },
  subtitle: {
    color: '#6c757d',
    fontSize: '0.875rem',
    lineHeight: '1.5'
  },
  inputSection: {
    backgroundColor: '#f8f9fa',
    padding: '1.25rem',
    borderRadius: '6px',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  textareaContainer: {
    marginBottom: '1rem'
  },
  labelContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.375rem'
  },
  label: {
    fontWeight: '500',
    color: '#495057',
    fontSize: '0.875rem'
  },
  clearButton: {
    padding: '0.25rem 0.75rem',
    border: '1px solid #e9ecef',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    color: '#6c757d',
    fontWeight: '500',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s'
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '0.8125rem',
    fontFamily: 'monospace',
    resize: 'vertical',
    minHeight: '180px',
    backgroundColor: 'white'
  },
  buttonContainer: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center'
  },
  button: {
    padding: '0.5rem 1.25rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: '500',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)'
  },
  primaryButton: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: 'white'
  },
  dryRunButton: {
    backgroundColor: '#28a745',
    color: 'white'
  },
  disabledButton: {
    backgroundColor: '#e9ecef',
    color: '#adb5bd',
    cursor: 'not-allowed',
    boxShadow: 'none'
  },
  errorContainer: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '0.75rem',
    marginBottom: '1.5rem'
  },
  errorTitle: {
    color: '#721c24',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '600'
  },
  errorText: {
    color: '#721c24',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.8125rem'
  },
  resultsContainer: {
    backgroundColor: 'white',
    padding: '1.5rem'
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem'
  },
  resultsTitle: {
    color: '#495057',
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  summaryContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.25rem'
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    borderRadius: '6px',
    textAlign: 'center',
    border: '1px solid #e9ecef',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  summaryTitle: {
    fontSize: '0.8125rem',
    color: '#6c757d',
    marginBottom: '0.375rem',
    fontWeight: '500'
  },
  summaryNumber: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#495057'
  },
  existingContainer: {
    marginBottom: '1.5rem'
  },
  accountsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  accountCard: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '0.5rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  accountHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem'
  },
  accountInfo: {
    flex: 1
  },
  accountName: {
    margin: '0 0 0.25rem 0',
    color: '#495057',
    fontSize: '1rem',
    fontWeight: '600'
  },
  accountEmail: {
    margin: '0 0 0.125rem 0',
    color: '#6c757d',
    fontSize: '0.875rem'
  },
  accountId: {
    margin: '0',
    color: '#adb5bd',
    fontSize: '0.75rem',
    fontFamily: 'monospace'
  },
  enrollmentStatus: {
    marginLeft: '0.75rem'
  },
  activeEnrollmentBadge: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #c8e6c9',
    borderRadius: '4px',
    padding: '0.375rem 0.75rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  activeText: {
    color: '#2e7d32',
    fontWeight: '500',
    fontSize: '0.75rem'
  },
  inactiveEnrollmentBadge: {
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    borderRadius: '4px',
    padding: '0.375rem 0.75rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  inactiveText: {
    color: '#c62828',
    fontWeight: '500',
    fontSize: '0.75rem'
  },
  enrollmentDetails: {
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '0.75rem',
    marginBottom: '0.75rem'
  },
  enrollmentTitle: {
    margin: '0 0 0.5rem 0',
    color: '#495057',
    fontSize: '0.875rem',
    borderBottom: '1px solid #e9ecef',
    paddingBottom: '0.375rem',
    fontWeight: '600'
  },
  enrollmentInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.375rem'
  },
  enrollmentRow: {
    fontSize: '0.8125rem',
    color: '#6c757d'
  },
  accountActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem'
  },
  viewAccountButton: {
    padding: '0.375rem 0.75rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    textDecoration: 'none',
    display: 'inline-block',
    fontWeight: '500',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
  },
  newContainer: {
    marginBottom: '1.5rem'
  },
  sectionTitle: {
    color: '#495057',
    marginBottom: '0.75rem',
    paddingBottom: '0.375rem',
    borderBottom: '1px solid #dee2e6',
    fontSize: '1rem',
    fontWeight: '600'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    fontSize: '0.8125rem'
  },
  th: {
    backgroundColor: '#6c757d',
    color: 'white',
    padding: '0.5rem 0.75rem',
    textAlign: 'left',
    border: '1px solid #5a6268',
    fontWeight: '500',
    fontSize: '0.8125rem'
  },
  tr: {
    borderBottom: '1px solid #e9ecef'
  },
  td: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e9ecef',
    color: '#495057'
  },
  // Dry Run Styles
  dryRunContainer: {
    backgroundColor: 'white',
    padding: '1.5rem'
  },
  dryRunHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem'
  },
  dryRunTitle: {
    color: '#495057',
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: '600'
  },
  dryRunErrors: {
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '0.75rem',
    marginBottom: '1.25rem'
  },
  dryRunErrorTitle: {
    color: '#721c24',
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: '600'
  },
  dryRunErrorList: {
    margin: 0,
    paddingLeft: '1.25rem'
  },
  dryRunErrorItem: {
    color: '#721c24',
    marginBottom: '0.125rem',
    fontSize: '0.8125rem'
  },
  dryRunSummary: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '0.75rem',
    marginBottom: '1.25rem',
    textAlign: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  dryRunCounts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.5rem',
    marginTop: '0.75rem'
  },
  dryRunCountItem: {
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '0.5rem',
    fontSize: '0.8125rem',
    color: '#495057'
  },
  dryRunAccounts: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1.5rem'
  },
  dryRunAccountCard: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '1rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  dryRunAccountHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #e9ecef'
  },
  dryRunAccountInfo: {
    flex: 1
  },
  dryRunAccountName: {
    margin: '0 0 0.25rem 0',
    color: '#495057',
    fontSize: '1rem',
    fontWeight: '600'
  },
  dryRunAccountEmail: {
    margin: '0',
    color: '#6c757d',
    fontSize: '0.875rem'
  },
  dryRunActionBadge: {
    marginLeft: '1rem'
  },
  updateBadge: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  createBadge: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  newAccountBadge: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  noUpdateBadge: {
    backgroundColor: '#e9ecef',
    color: '#6c757d',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    marginLeft: '0.5rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  accountUpdateBadge: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    marginRight: '0.5rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  accessCardUpdateBadge: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: '1px solid #ffc107',
    marginLeft: '0.5rem'
  },
  enrollmentUpdateBadge: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: 'none',
    marginRight: '0.5rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  filterContainer: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '1rem',
    marginBottom: '1.25rem',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  filterTitle: {
    margin: '0 0 0.75rem 0',
    color: '#495057',
    fontSize: '0.875rem',
    fontWeight: '600'
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.5rem',
    marginBottom: '0.75rem'
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    fontSize: '0.8125rem',
    color: '#6c757d',
    cursor: 'pointer'
  },
  dryRunChanges: {
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '0.75rem'
  },
  dryRunChangesTitle: {
    margin: '0 0 0.75rem 0',
    color: '#495057',
    fontSize: '0.875rem',
    borderBottom: '1px solid #e9ecef',
    paddingBottom: '0.375rem',
    fontWeight: '600'
  },
  dryRunChangeSection: {
    marginBottom: '0.75rem'
  },
  dryRunChangeSubtitle: {
    margin: '0 0 0.375rem 0',
    color: '#6c757d',
    fontSize: '0.8125rem',
    fontWeight: '600'
  },
  dryRunChangeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.125rem 0',
    borderBottom: '1px solid #f8f9fa'
  },
  dryRunChangeLabel: {
    fontWeight: '500',
    color: '#6c757d',
    minWidth: '100px',
    fontSize: '0.8125rem'
  },
  dryRunChangeValue: {
    color: '#495057',
    flex: 1,
    textAlign: 'right',
    fontFamily: 'monospace',
    fontSize: '0.75rem'
  },
  dryRunActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    paddingTop: '1rem',
    borderTop: '1px solid #dee2e6'
  },
  // Tab Styles
  tabsContainer: {
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '0',
    marginTop: '1.5rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  tabsHeader: {
    display: 'flex',
    borderBottom: '1px solid #e9ecef',
    backgroundColor: '#f8f9fa'
  },
  tabButton: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#6c757d',
    transition: 'all 0.2s',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px'
  },
  activeTabButton: {
    color: '#495057',
    borderBottomColor: '#495057',
    backgroundColor: 'white',
    fontWeight: '600'
  },
  // Bookmark Styles
  validationBookmarks: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e9ecef'
  },
  dryRunBookmarks: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    padding: '0.75rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    border: '1px solid #e9ecef',
    flexWrap: 'wrap'
  },
  bookmarkButton: {
    padding: '0.375rem 0.75rem',
    border: '1px solid #e9ecef',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    color: '#6c757d',
    transition: 'all 0.2s',
    fontWeight: '500',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
  },
  activeBookmarkButton: {
    backgroundColor: '#495057',
    color: 'white',
    borderColor: '#495057',
    fontWeight: '600',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  dryRunActionsTop: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-start',
    marginBottom: '1.25rem',
    paddingBottom: '0.75rem',
    borderBottom: '1px solid #e9ecef'
  },
  refreshButton: {
    padding: '0.5rem',
    border: '1px solid #e9ecef',
    backgroundColor: 'white',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6c757d',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 0.2s',
    marginTop: '0.75rem',
    width: '32px',
    height: '32px'
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  loadingContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '300px'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e9ecef',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem'
  },
  loadingText: {
    margin: '0 0 0.5rem 0',
    fontSize: '1rem',
    fontWeight: '600',
    color: '#495057'
  },
  loadingSubtext: {
    margin: 0,
    fontSize: '0.875rem',
    color: '#6c757d'
  },
  resultIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1rem'
  },
  resultMessage: {
    margin: '0 0 1rem 0',
    fontSize: '0.875rem',
    color: '#495057',
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
    maxWidth: '500px',
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e9ecef',
    fontFamily: 'inherit'
  }
};

export default CSVImport;
